// src/components/FloatingChatWidget.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box, Paper, Typography, IconButton, Avatar, Chip, TextField, Button, Fade
} from "@mui/material";
import { Close as CloseIcon, Chat as ChatIcon, Send as SendIcon } from "@mui/icons-material";

// Suggested prompts for Canadian criminal law
const SUGGESTED = [
  "Summarize Charter issues",
  "Draft s.24(2) motion",
  "Sentencing range for assault",
  "Show recent SCC cases",
  "Explain s. 10(b) right to counsel",
  "List bail release options"
];

// Initial welcome message
const INIT_MSGS = [
  {
    id: 1,
    sender: "ai",
    text: "I'm Lex Maple Copilot. Ask about charges, motions, sentencing, or leading cases."
  }
];

// Dummy AI logic
function getAIResponse(userInput) {
  if (/charter|24\(2\)/i.test(userInput))
    return "Section 24(2) of the Charter allows evidence to be excluded if its admission would bring the administration of justice into disrepute.";
  if (/assault/i.test(userInput))
    return "For adult assault: Conditional discharge up to 18 months probation is common; max is 5 years if indictable.";
  if (/10\(b\)/i.test(userInput))
    return "Section 10(b) guarantees the right to counsel on arrest or detention. Leading case: R. v. Grant.";
  if (/bail/i.test(userInput))
    return "Common bail release options: undertaking, recognizance, surety, or detention. See s. 515 of the Criminal Code.";
  return "Let me process your request. You can also ask for summaries, motion drafts, or sentencing ranges.";
}

export default function FloatingChatWidget({ messages, setMessages, onOpenFullChat }) {
  // Always fallback to INIT_MSGS if messages is undefined/null
  const [internalMessages, setInternalMessages] = useState(INIT_MSGS);
  const msgs = messages || internalMessages;
  const updateMsgs = setMessages || setInternalMessages;

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const chatBottomRef = useRef(null);

  useEffect(() => {
    if (open) chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    const userMsg = {
      id: Date.now() + Math.random(),
      sender: "user",
      text
    };
    updateMsgs(msgs => [...msgs, userMsg]);
    setInput("");
    setTimeout(() => {
      const aiText = getAIResponse(text);
      updateMsgs(msgs => [
        ...msgs,
        { id: Date.now() + Math.random(), sender: "ai", text: aiText }
      ]);
    }, 700 + Math.random() * 700);
  };

  const handleSuggestion = (txt) => setInput(txt);

  const showChips = (msgs || []).filter(m => m.sender === "user").length === 0;

  if (!open)
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 28,
          right: 34,
          zIndex: 1004
        }}
      >
        <Fade in={!open}>
          <IconButton
            size="large"
            onClick={() => setOpen(true)}
            sx={{
              bgcolor: "#204062",
              color: "#fff",
              width: 68,
              height: 68,
              boxShadow: "0 4px 28px 0 rgba(32,64,98,0.23)",
              "&:hover": { bgcolor: "#315382" }
            }}
          >
            <ChatIcon sx={{ fontSize: 38 }} />
          </IconButton>
        </Fade>
      </Box>
    );

  // Main popup chat window
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 30,
        zIndex: 1300,
        width: 384,
        maxWidth: "97vw",
        height: 620,
        maxHeight: "93vh",
        boxShadow: "0 6px 32px 0 rgba(32,64,98,0.14)",
        borderRadius: "34px",
        overflow: "hidden",
        background: "#f8fafc",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Header */}
      <Box sx={{
        bgcolor: "#204062",
        color: "#fff",
        py: 2.1,
        px: 2.7,
        display: "flex",
        alignItems: "center",
        gap: 1.6,
        minHeight: 44
      }}>
        <Avatar
          sx={{
            bgcolor: "#fff",
            color: "#204062",
            fontWeight: 700,
            fontSize: 26,
            width: 50,
            height: 50,
            mr: 1
          }}
        >LM</Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 25, letterSpacing: 0.2, flex: 1 }}>
          Lex Maple Copilot
        </Typography>
        {onOpenFullChat && (
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: "#fff",
              borderColor: "#fff",
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              mr: 2,
              ml: 1,
              "&:hover": { bgcolor: "#1a3551", borderColor: "#fff" }
            }}
            onClick={() => {
              setOpen(false);
              onOpenFullChat && onOpenFullChat(msgs);
            }}
          >
            Full Chat
          </Button>
        )}
        <IconButton onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Suggestions */}
      {showChips && (
        <Box sx={{ px: 2.2, pt: 2.2, pb: 0.7, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {SUGGESTED.map((s, i) => (
            <Chip
              key={s}
              label={s}
              onClick={() => handleSuggestion(s)}
              clickable
              size="small"
              sx={{
                bgcolor: "#eaf0fa",
                color: "#204062",
                fontWeight: 700,
                fontSize: 16,
                mb: 0.4,
                "&:hover": { bgcolor: "#d7e3f5" }
              }}
            />
          ))}
        </Box>
      )}

      {/* Chat area */}
      <Box
        sx={{
          flex: 1,
          p: 2.2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          maxHeight: "100%",
        }}
      >
        {(msgs || []).map(msg => (
          <Box
            key={msg.id}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: msg.sender === "user" ? "row-reverse" : "row"
            }}
          >
            <Avatar
              sx={{
                bgcolor: msg.sender === "ai" ? "#204062" : "#3ad28d",
                color: "#fff",
                fontWeight: 700,
                fontSize: 18,
                width: 38,
                height: 38,
                mr: msg.sender === "ai" ? 1.2 : 0,
                ml: msg.sender === "user" ? 1.2 : 0
              }}
            >
              {msg.sender === "ai" ? "LM" : "U"}
            </Avatar>
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.6,
                bgcolor: msg.sender === "ai" ? "#e7f0fa" : "#e8faed",
                color: "#22405b",
                borderRadius: msg.sender === "ai"
                  ? "18px 18px 18px 6px"
                  : "18px 18px 6px 18px",
                fontSize: 18.2,
                maxWidth: 310,
                boxShadow: "0 2px 14px 0 rgba(32,64,98,0.09)"
              }}
            >
              {msg.text}
            </Paper>
          </Box>
        ))}
        <div ref={chatBottomRef} />
      </Box>

      {/* Input */}
      <Box sx={{
        px: 2,
        pb: 2,
        pt: 1,
        display: "flex",
        alignItems: "center",
        bgcolor: "#f6fafd"
      }}>
        <TextField
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Ask a legal question..."
          fullWidth
          size="medium"
          variant="outlined"
          sx={{
            bgcolor: "#f2f7fa",
            borderRadius: 2.5,
            fontSize: 18,
            mr: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3
            }
          }}
          InputProps={{
            style: { fontSize: 18 }
          }}
        />
        <Button
          variant="contained"
          sx={{
            bgcolor: "#204062",
            color: "#fff",
            borderRadius: 2,
            minWidth: 54,
            height: 48,
            ml: 1.3,
            boxShadow: "none",
            "&:hover": { bgcolor: "#315382" }
          }}
          onClick={handleSend}
          disabled={!input.trim()}
        >
          <SendIcon sx={{ fontSize: 27, ml: -.6 }} />
        </Button>
      </Box>
    </Box>
  );
}
